import React, { useState } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { CohortPersonDetails } from '../cohortPageData'
import { PartnerService, UserService } from '@duck/api-definitions'
import { RelativeDateTime } from '../../datetime/RelativeDateTime'
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import { SendAssignment } from './SendAssignment'
import { DeleteDialog } from '../../DeleteDialog'
import { apis } from '@/modules/apiClients'
import { toast } from 'sonner'
import { AssignmentProgressTable } from './AssignmentProgressTable'

interface AssignmentEntryProps {
  assignment: PartnerService.Assignment
  peopleDetails: CohortPersonDetails[]
  assignmentPersons: UserService.Person[]
  getDisplayName: (taskId: string) => string
  isTaskLoading: boolean
  onAssignmentChange?: () => void
}

export const AssignmentEntry: React.FC<AssignmentEntryProps> = ({
  assignment,
  peopleDetails,
  assignmentPersons,
  getDisplayName,
  isTaskLoading,
  onAssignmentChange,
}) => {
  const { t } = useTar()
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const completionRate = peopleDetails.length > 0
    ? (assignment.submissions || 0) / peopleDetails.length
    : 0

  const handleEdit = () => {
    setIsEditDialogOpen(true)
  }

  const handleDelete = async () => {
    try {
      await apis.partnerService.assignmentsAssignmentIdDelete(assignment.id)
      toast.success(t('common.assignmentDeleted'))
      onAssignmentChange?.()
    } catch (error) {
      console.error('Failed to delete assignment:', error)
      throw error
    }
  }

  return (
    <Card>
      <CardHeader className="p-4 md:p-6">
        <div className="flex items-start justify-between gap-4">
          <CardTitle className="flex-1 text-xl font-semibold">
            {isTaskLoading ? (
              <span className="text-lg font-semibold">{t('common.loading')}</span>
            ) : (
              getDisplayName(assignment.taskId)
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Three-dot dropdown menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <EllipsisVerticalIcon className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleEdit}>
                  {t('common.edit')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="text-destructive focus:text-destructive"
                >
                  {t('common.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <CardDescription className="space-y-3 pt-2">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row">
              <div className="flex flex-col gap-2 font-bold text-foreground">{t('partner.timeWindow')}:</div>
              <div className="text-right flex-1">
                <div className="flex-row flex items-center justify-end gap-2">
                  <div className="hidden md:flex items-center text-sm">
                    <span className="font-medium">{t('partner.completionRate')}:</span>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5">
                          <svg width="24" height="24" viewBox="0 0 24 24" className="transform -rotate-90">
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="4"
                              className="text-muted/30"
                            />
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="4"
                              strokeDasharray={`${completionRate * 62.83} 62.83`}
                              strokeLinecap="round"
                              className="text-primary"
                            />
                          </svg>
                          <span className="text-xs text-muted-foreground">
                            {assignment.submissions || 0}/{peopleDetails.length}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <span>{Math.round(completionRate * 100)}% {t('partner.completionRate').toLowerCase()}</span>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>
            <div className="flex flex-col lg:flex-row flex-wrap gap-2 lg:gap-4 text-sm">
              {assignment.startDate && (
                <div>
                  <span className="font-medium">{t('partner.startDate')}:</span>{' '}
                  <RelativeDateTime
                    isoDateString={assignment.startDate}
                    className="text-muted-foreground"
                  />
                </div>
              )}
              {assignment.dueDate && (
                <div>
                  <span className="font-medium">{t('partner.dueDate')}:</span>{' '}
                  <RelativeDateTime
                    isoDateString={assignment.dueDate}
                    className="text-muted-foreground"
                  />
                </div>
              )}
              {assignment.overdueDate && (
                <div>
                  <span className="font-medium">{t('partner.overdueDate')}:</span>{' '}
                  <RelativeDateTime
                    isoDateString={assignment.overdueDate}
                    className="text-muted-foreground"
                  />
                </div>
              )}
              {!assignment.startDate && !assignment.dueDate && !assignment.overdueDate && (
                <div className="text-muted-foreground">{t('partner.noRestriction')}</div>
              )}
            </div>
          </div>
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 md:p-6">
        {peopleDetails.length > 0 ? (
          <AssignmentProgressTable
            peopleDetails={peopleDetails}
            persons={assignmentPersons}
            sentTime={assignment.createdAt || ''}
          />
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">{t('common.empty')}</p>
          </div>
        )}
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent width="xs">
          <DialogHeader>
            <DialogTitle>{t('common.editAssignment')}</DialogTitle>
          </DialogHeader>
          <SendAssignment
            cohortId={assignment.cohortId}
            personIds={assignment.personIds}
            assignmentId={assignment.id}
            onClose={() => setIsEditDialogOpen(false)}
            onSend={() => {
              setIsEditDialogOpen(false)
              onAssignmentChange?.()
            }}
          />
        </DialogContent>
      </Dialog>

      <DeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
        title={t('common.deleteAssignment')}
        description={t('common.assignmentDeletedDescription')}
      />
    </Card>
  )
}
